import { TransactionFeatureToggles } from "@/modules/transaction/common/enum";
import { useGetTransactionTypesQuery } from "@/modules/transaction/services";
import { OperationRoutes } from "@/shared/common/constants";
import { CloudflareAsset } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { ActionItem, CloudFlareImage, ListContentWrapper } from "@/shared/components";
import { useAppSelector, useScroll } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const MAX_COUNT_ROW = 7;
const MAX_COUNT = 15;

export const Transactions = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toggles, isLoaded } = useAppSelector((state) => state.featureToggle);

  const {
    data: transactionTypesList,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetTransactionTypesQuery({}, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      data: data?.filter((transactionType) => {
        return toggles?.[TransactionFeatureToggles[transactionType as keyof typeof TransactionFeatureToggles]];
      })
    })
  });

  const {
    isAtEnd,
    isAtStart,
    scrollContainerRef,
    onHorizontalScroll,
  } = useScroll();

  const transactionTypes = useMemo(() => {
    const slicedList = transactionTypesList?.length && transactionTypesList.length > MAX_COUNT ?
      transactionTypesList.slice(0, MAX_COUNT) : transactionTypesList || [];

    const firstPart = slicedList.length > MAX_COUNT_ROW ?
      slicedList.slice(0, MAX_COUNT_ROW) : slicedList;

    const secondPart = slicedList.length > MAX_COUNT_ROW ?
      slicedList.slice(MAX_COUNT_ROW) : [];

    return ({
      firstPart,
      secondPart,
    });
  }, [transactionTypesList, isLoaded]);

  return (
    <div className="full-width transactions">
      <ListContentWrapper
        isError={isError}
        isFetching={isFetching}
        isEmpty={!transactionTypesList?.length}
        error={error as IError}
        refetch={refetch}
      >
        <div className="position-relative">
          <div ref={scrollContainerRef} className="service-items-wrapper mb-24">
            {!isAtStart && (
              <div className="arrow-wrapper left-overlay">
                <Button
                  onClick={() => onHorizontalScroll("left")}
                  type="secondary"
                  size="medium"
                  data-testid="transaction-right-scroll"
                  data-ga-id="transaction-right-scroll"
                  data-ga-action="transactions widget right scroll button"
                  iconProps={{
                    Component: (props: ISVGIconProps) => (
                      <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortLeft}/>
                    ),
                  }}
                />
              </div>
            )}
            <div className="full-width flexbox">
              {transactionTypes.firstPart.map((transactionType) => (
                <div key={transactionType} className="pr-24 pb-24">
                  <ActionItem
                    title={t(`banking.transactions.${transactionType}`)}
                    icon={transactionType as CloudflareAsset}
                    onClick={() => navigate(OperationRoutes[transactionType])}
                  />
                </div>
              ))}
            </div>
            <div className="full-width flexbox">
              {transactionTypes.secondPart?.map((transactionType) => (
                <div key={transactionType} className="pr-24 pb-24">
                  <ActionItem
                    title={transactionType}
                    icon={transactionType as CloudflareAsset}
                    onClick={() => navigate(OperationRoutes[transactionType])}
                  />
                </div>
              ))}
            </div>
            {!isAtEnd && (
              <div className="arrow-wrapper right-overlay">
                <Button
                  onClick={() => onHorizontalScroll("right")}
                  type="secondary"
                  size="medium"
                  iconProps={{
                    Component: (props: ISVGIconProps) => (
                      <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortRight}/>
                    ),
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </ListContentWrapper>
    </div>
  );
};
